import React, { useEffect, useRef, useState } from "react"
import {
	Button,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Grid,
	Link,
	Radio,
	RadioGroup,
	Typography
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import FolderOpenIcon from "@material-ui/icons/FolderOpen"
import TableChartIcon from "@material-ui/icons/TableChart"
import CloseIcon from "@material-ui/icons/Close"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"

import { Loading, Portlet } from "@/components"
import ImportContactsSkeleton from "@/skeletons/Admin/ImportContactsSkeleton"
import { letalkLinks } from "@/utils/link"
import { beautifyBytes } from "@/utils/format"
import { detectContactsColumns } from "@/services/Api"
import { DetectedColumnsType, FileMetadataType } from "@/protocols/clientCatalogImportProtocol"
import useStyles from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactByCSV/styles"

interface ImportContactsFormValuesType {
	duplicate_contacts_action: "ignore" | "replace"
	contacts_file?: File
}

interface ValidationErrors {
	[key: string]: string
}

interface ImportContactsByCSVProps {
	loading: boolean
	isContactsToImportBeingProcessed: boolean
	totalContactsProcessed: number
	importContactsFormValues: ImportContactsFormValuesType
	validation: ValidationErrors
	handleContactsToImportFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
	handleImportContactsByFile: () => void
	ContactsBeingProcessedScreen: React.ComponentType<{ totalContactsProcessed: number }>
	onDetectionComplete?: (
		result: DetectedColumnsType,
		file: File,
		duplicateAction: ImportContactsFormValuesType["duplicate_contacts_action"],
		fileMetadata: FileMetadataType
	) => void
	onFileDrop?: (file: File | null) => void
}

const extractCsvFileMetadata = (csvFile: File): Promise<FileMetadataType> => {
	return new Promise((resolve) => {
		const reader = new FileReader()

		reader.onload = (loadEvent) => {
			const csvText = loadEvent.target?.result as string
			const filledLines = csvText.split("\n").filter((line) => line.trim())
			const contactLineCount = Math.max(0, filledLines.length - 1)
			const headerLine = filledLines[0] || ""
			const commaColumnCount = headerLine.split(",").length
			const semicolonColumnCount = headerLine.split(";").length
			const detectedColumnCount = Math.max(commaColumnCount, semicolonColumnCount)

			resolve({
				lines: contactLineCount,
				columns: detectedColumnCount,
				size: beautifyBytes(csvFile.size)
			})
		}

		reader.readAsText(csvFile)
	})
}

const ImportContactsByCSV = (props: ImportContactsByCSVProps) => {
	const {
		loading,
		isContactsToImportBeingProcessed,
		totalContactsProcessed,
		importContactsFormValues,
		validation,
		handleContactsToImportFileInputChange,
		handleImportContactsByFile,
		ContactsBeingProcessedScreen,
		onDetectionComplete,
		onFileDrop
	} = props

	const classes = useStyles()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [fileMetadata, setFileMetadata] = useState<FileMetadataType | null>(null)

	const hasFile = !!importContactsFormValues.contacts_file

	useEffect(() => {
		const selectedFile = importContactsFormValues.contacts_file

		if (!selectedFile) {
			setFileMetadata(null)
			return
		}

		extractCsvFileMetadata(selectedFile).then(setFileMetadata)
	}, [importContactsFormValues.contacts_file])

	const handleDragOver = (dragEvent: React.DragEvent) => {
		dragEvent.preventDefault()
		setIsDragging(true)
	}

	const handleDragLeave = () => {
		setIsDragging(false)
	}

	const handleDrop = (dropEvent: React.DragEvent) => {
		dropEvent.preventDefault()
		setIsDragging(false)
		const file = dropEvent.dataTransfer.files[0]
		const isCsvFile = !!file && (file.name.endsWith(".csv") || file.type === "text/csv")

		if (isCsvFile) {
			onFileDrop?.(file)
		}
	}

	const handleClickDropZone = () => {
		fileInputRef.current?.click()
	}

	const handleRemoveFile = (clickEvent: React.MouseEvent) => {
		clickEvent.stopPropagation()
		onFileDrop?.(null)

		if (fileInputRef.current) {
			fileInputRef.current.value = ""
		}
	}

	const handleDetectColumns = async () => {
		if (!importContactsFormValues.contacts_file) {
			handleImportContactsByFile()
			return
		}
		try {
			const formData = new FormData()
			formData.append("contactsFile", importContactsFormValues.contacts_file)
			formData.append("duplicateContactsAction", importContactsFormValues.duplicate_contacts_action)
			const response = await detectContactsColumns(formData)

			if (onDetectionComplete) {
				onDetectionComplete(
					response.data as DetectedColumnsType,
					importContactsFormValues.contacts_file,
					importContactsFormValues.duplicate_contacts_action,
					fileMetadata || { lines: 0, columns: 0, size: "0 Bytes" }
				)
			}
		} catch (error) {
			handleImportContactsByFile()
		}
	}

	return (
		<Grid container>
			<Loading loading={loading} customLoadingElement={<ImportContactsSkeleton />}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Portlet>
							{isContactsToImportBeingProcessed && (
								<ContactsBeingProcessedScreen totalContactsProcessed={totalContactsProcessed} />
							)}

							{!isContactsToImportBeingProcessed && (
								<Grid container direction="column" spacing={3}>
									<Grid item>
										<Alert icon={false} severity="warning">
											Os números devem ser enviados com DDI, DDD e 9° dígito. Ex.: <b>55 11 9 94544758</b> (desconsideramos qualquer caracter que não seja número) <br />
											O arquivo enviado deve ter na 1ª linha as seguintes colunas: <b>nome</b>, <b>numero</b> e <b>email</b> (esta última coluna é opcional) <br />
											Tipo de arquivo: <b>CSV</b> <br />
											Limite máximo do arquivo: <b>1MB</b> (Cerca de 25.000 contatos) <br /><br />

											É obrigatório o preenchimento das colunas <b>nome</b> e <b>numero</b> nos contatos que serão importados. O preenchimento da coluna <b>email</b> não é obrigatório, mesmo que a coluna exista no cabeçalho da planilha.<br />
											<b>IMPORTANTE:</b> contatos que não possuam <b>nome</b> e <b>número</b>, ambos preenchidos, não serão considerados como contatos encontrados no arquivo. Portanto, não serão importados e também não estarão na planilha ao realizar o download dos contatos não importados.<br /><br />

											<Link
												href={letalkLinks.wikiHowToImportContacts}
												underline="always"
												target="_blank"
												color="inherit"
											>
												Clique aqui para acessar o tutorial completo de como o arquivo enviado deve ser configurado.
											</Link>
										</Alert>
									</Grid>

									<Grid item>
										<Grid container direction="column" spacing={3}>
											<Grid item>
												<FormControl style={{ width: "100%" }}>
													<FormLabel><b>ARQUIVO</b></FormLabel>
													<div style={{ marginTop: 8 }}>
														<input
															ref={fileInputRef}
															id="contacts_file"
															name="contacts_file"
															type="file"
															accept=".csv"
															style={{ display: "none" }}
															onChange={handleContactsToImportFileInputChange}
														/>

														{!hasFile && (
															<div
																className={`${classes.dropZone} ${isDragging ? classes.dropZoneDragging : ""}`}
																onDragOver={handleDragOver}
																onDragLeave={handleDragLeave}
																onDrop={handleDrop}
																onClick={handleClickDropZone}
															>
																<div className={classes.dropZoneLeft}>
																	<CloudUploadIcon className={classes.dropZoneIcon} />
																	<div>
																		<Typography className={classes.dropZoneTextPrimary}>
																			Escolha um arquivo para importar
																		</Typography>
																		<Typography className={classes.dropZoneTextSecondary}>
																			Arraste aqui ou clique em selecionar — apenas .CSV até 1MB
																		</Typography>
																	</div>
																</div>
																<Button
																	variant="contained"
																	className={classes.selectButton}
																	startIcon={<FolderOpenIcon />}
																	onClick={(clickEvent) => {
																		clickEvent.stopPropagation()
																		fileInputRef.current?.click()
																	}}
																	disableElevation
																>
																	Selecionar arquivo
																</Button>
															</div>
														)}

														{hasFile && (
															<div className={classes.selectedRow}>
																<TableChartIcon className={classes.selectedFileIcon} />
																<div className={classes.selectedFileInfo}>
																	<Typography className={classes.selectedFileName}>
																		{importContactsFormValues.contacts_file?.name}
																	</Typography>
																	{fileMetadata && (
																		<Typography className={classes.selectedFileMeta}>
																			{fileMetadata.lines.toLocaleString("pt-BR")} linhas · {fileMetadata.columns} colunas detectadas · {fileMetadata.size}
																		</Typography>
																	)}
																</div>
																<Button
																	className={classes.removeButton}
																	startIcon={<CloseIcon />}
																	onClick={handleRemoveFile}
																	disableRipple
																>
																	Remover
																</Button>
															</div>
														)}

														{!!validation.contacts_file && (
															<FormHelperText error>{validation.contacts_file}</FormHelperText>
														)}
													</div>
												</FormControl>
											</Grid>

											<Grid item>
												<FormControl focused={false}>
													<FormLabel><b>O que deseja fazer quando for identificado número já cadastrado na Letalk?</b></FormLabel>
													<RadioGroup
														aria-labelledby="demo-error-radios"
														name="duplicate_contacts_action"
														value={importContactsFormValues.duplicate_contacts_action}
														onChange={handleContactsToImportFileInputChange}
													>
														<FormControlLabel value="replace" control={<Radio />} label="Atualizar o cadastro na Letalk" />
														<FormControlLabel value="ignore" control={<Radio />} label="Ignorar Número" />
													</RadioGroup>
												</FormControl>
											</Grid>
										</Grid>
									</Grid>

									<Grid item>
										<Grid container justifyContent="flex-end">
											<Grid item>
												<Button
													color="primary"
													variant="contained"
													disabled={!hasFile}
													onClick={handleDetectColumns}
												>
													IMPORTAR
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							)}
						</Portlet>
					</Grid>
				</Grid>
			</Loading>
		</Grid>
	)
}

export default ImportContactsByCSV
